// src/components/ShortcutCheatsheet.jsx
//
// Global keyboard cheatsheet dialog. Opens on `?` (Shift+/ on US
// keyboards, just `?` on many others). Lists every shortcut the
// portal registers so new users can orient themselves without reading
// docs.

import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Keyboard as KeyboardIcon } from '@mui/icons-material';

// Platform hint — on macOS show ⌘, elsewhere Ctrl.
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const mod = isMac ? '⌘' : 'Ctrl';

const SECTIONS = [
  {
    label: 'Global',
    items: [
      { keys: [`${mod}+K`], description: 'Open command palette — jump to any page or toggle a preference' },
      { keys: ['?'], description: 'Show this cheatsheet' },
      // 20260925-ui-feedback (V-11). Works inside text fields too; only for users who
      // have the feedback button (ideas.ui_feedback + ideas.create).
      { keys: [`${mod}+.`], description: 'Open UI feedback' },
      { keys: [`${mod}+S`], description: 'Save (when editing a page that supports it)' },
      { keys: ['Esc'], description: 'Close the open dialog, drawer, or menu' },
    ],
  },
  {
    label: 'Navigation',
    items: [
      { keys: [`${mod}+B`], description: 'Toggle the sidebar between rail and wide' },
      { keys: ['G then H'], description: 'Go to Home / Dashboard' },
      { keys: ['G then A'], description: 'Go to Alarms' },
      { keys: ['G then T'], description: 'Go to Tickets' },
      { keys: ['G then L'], description: 'Go to Locations' },
      { keys: ['G then N'], description: 'Go to Notifications' },
      { keys: ['G then P'], description: 'Go to Persons' },
      { keys: ['G then D'], description: 'Go to Devices' },
    ],
  },
  {
    label: 'Automation Editor',
    items: [
      { keys: [`${mod}+K`], description: 'Block / node command palette' },
      { keys: [`${mod}+Z`, `${mod}+Shift+Z`], description: 'Undo / redo' },
      { keys: [`${mod}+C`, `${mod}+V`, `${mod}+D`], description: 'Copy / paste / duplicate selection' },
      { keys: ['N', 'Shift+N'], description: 'Jump to next / previous validation issue' },
      { keys: ['F'], description: 'Fit canvas to view' },
      { keys: ['Shift+F'], description: 'Canvas-only fullscreen (hide palette + inspector)' },
      { keys: ['Delete'], description: 'Delete selected nodes and edges' },
    ],
  },
  {
    label: 'Detail drawers',
    items: [
      { keys: ['Shift+F'], description: 'Toggle fullscreen on an open detail drawer' },
      { keys: ['Esc'], description: 'Close the drawer' },
    ],
  },
  {
    label: 'Lists and search',
    items: [
      { keys: ['/'], description: 'Focus the search field (coming soon)' },
      { keys: ['↑', '↓'], description: 'Move between rows in the command palette' },
      { keys: ['Enter'], description: 'Open the highlighted result' },
    ],
  },
];

export const useShortcutCheatsheet = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      // Do not hijack ? while typing in an input.
      const t = e.target;
      const tag = t?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || t?.isContentEditable) return;
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
};

const KeyChip = ({ label }) => (
  <Chip
    size="small"
    variant="outlined"
    label={label}
    sx={{
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
      fontSize: '0.78rem',
      fontWeight: 600,
      height: 24,
      borderRadius: 1,
      bgcolor: 'background.paper',
      borderColor: 'divider',
      px: 0.5,
    }}
  />
);

const ShortcutCheatsheet = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <KeyboardIcon color="primary" />
        <Typography variant="h6">Keyboard shortcuts</Typography>
      </Stack>
      <IconButton onClick={onClose} aria-label="Close">
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Stack spacing={2}>
        {SECTIONS.map((section) => (
          <Box key={section.label}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'text.secondary',
                display: 'block',
                mb: 1,
              }}
            >
              {section.label}
            </Typography>
            <Stack spacing={0.75}>
              {section.items.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <Stack direction="row" spacing={0.5} sx={{ minWidth: 130 }}>
                    {row.keys.map((k, j) => (
                      <React.Fragment key={`${i}-${j}`}>
                        <KeyChip label={k} />
                        {j < row.keys.length - 1 && (
                          <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>
                            or
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {row.description}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        Want a shortcut that isn't here? Let us know in the Help page.
      </Typography>
    </DialogContent>
  </Dialog>
);

export default ShortcutCheatsheet;
